import React, { useCallback, useMemo, useState } from 'react'
import Backdrop from '../UI/Backdrop'
import { useTranslation } from 'react-i18next'
import PlanCard from '../Subscription/Plan/PlanCard'
import { planFeatures } from '../../helpers/pricingTiers'
import PlanProductType from '../Subscription/Plan/PlanProductType'
import PlanSlider from '../Subscription/Plan/PlanSlider'
import { Subscription } from '../../interfaces/subscription'
import {
  IntervalType,
  PricingPlan,
  ProductType,
} from '../../interfaces/pricingPlan'

interface Props {
  onClose: () => void
  activeSubscription: Subscription
  pricingPlans: PricingPlan[]
}

const ChangePlanModal = ({
  onClose,
  activeSubscription,
  pricingPlans = [],
}: Props) => {
  const { t } = useTranslation()
  const [selectedPlanId, setSelectedPlanId] = useState<number>(
    activeSubscription.pricing_plan.id,
  )
  const [productType, setProductType] = useState<ProductType | null>(
    activeSubscription.pricing_plan.product.product_type,
  )
  const [intervalType, setIntervalType] = useState<IntervalType>(
    activeSubscription.pricing_plan.product.interval_type,
  )

  const getFilteredPlans = useCallback(
    (productType: ProductType | null, intervalType: IntervalType) => {
      const filteredPlans =
        pricingPlans?.filter(
          (plan) =>
            plan.product.product_type === productType &&
            plan.product.interval_type === intervalType,
        ) ?? []
      filteredPlans?.sort((a, b) => +a.amount - +b.amount)
      return filteredPlans
    },
    [pricingPlans],
  )

  const filteredPlans = getFilteredPlans(productType, intervalType)

  useMemo(
    () => filteredPlans.sort((a, b) => +a.amount - +b.amount),
    [filteredPlans],
  )
  const unit =
    productType === 'company' ? t('employeesUnit') : t('studentsUnit')

  const selectedPlan =
    filteredPlans?.find((plan) => plan.id === selectedPlanId) ??
    activeSubscription.pricing_plan
  const isActivePlan = selectedPlanId === activeSubscription.pricing_plan.id

  const currentIndex = filteredPlans.findIndex(
    (pricingPlan) =>
      pricingPlan.product.base_name.toLowerCase() ===
      selectedPlan.product.base_name.toLowerCase(),
  )

  // Every one of these used to index the filtered list without checking it
  // had the entry: `[0].id` on an empty list, and `[currentIndex].id` where
  // `findIndex` had already returned -1. Both throw inside an event handler,
  // and there is no error boundary above this modal to catch it.
  const handleSliderCommit = (index: number) => {
    const plan = filteredPlans[index]
    if (plan) setSelectedPlanId(plan.id)
  }

  const handleProductTypeChange = (nextProductType: ProductType) => {
    const nextPlans = getFilteredPlans(nextProductType, intervalType)
    // Switching to a type with no plans would leave an empty slider with
    // nothing selected, so stay put instead.
    if (nextPlans.length === 0) return
    setSelectedPlanId(nextPlans[0].id)
    setProductType(nextProductType)
  }

  const handleIntervalTypeChange = (nextIntervalType: IntervalType) => {
    const nextPlans = getFilteredPlans(productType, nextIntervalType)
    // Carry the slider position across, falling back to the cheapest plan
    // when the two lists are not the same length.
    const nextPlan = nextPlans[currentIndex] ?? nextPlans[0]
    if (!nextPlan) return
    setSelectedPlanId(nextPlan.id)
    setIntervalType(nextIntervalType)
  }
  return (
    <>
      <Backdrop />
      <div
        onClick={onClose}
        className='h-dvh min-h-dvh overflow-y-auto flex flex-col fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full p-5'
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className='flex flex-col z-50 m-auto bg-white max-w-[1065px] w-full rounded-lg p-5 sm:py-6 sm:px-10'
        >
          <button onClick={onClose} type='button' className='ml-auto group'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='25'
              height='25'
              viewBox='0 0 25 25'
              fill='none'
            >
              <path
                d='M13.437 12.4962L20.472 5.45428C20.5961 5.32955 20.6657 5.16073 20.6656 4.98477C20.6655 4.80882 20.5956 4.64009 20.4714 4.51553C20.2226 4.26803 19.7845 4.26678 19.5332 4.51678L12.5001 11.5587L5.46449 4.5149C5.21449 4.26803 4.77636 4.26928 4.52761 4.51615C4.46587 4.57765 4.41699 4.65083 4.38384 4.73141C4.35068 4.812 4.3339 4.89839 4.33449 4.98553C4.33449 5.16303 4.40324 5.32928 4.52761 5.4524L11.5626 12.4955L4.52824 19.5393C4.4041 19.6642 4.33459 19.8332 4.33494 20.0094C4.33529 20.1855 4.40548 20.3542 4.53011 20.4787C4.65074 20.598 4.82136 20.6668 4.99761 20.6668H5.00136C5.17824 20.6662 5.34886 20.5968 5.46699 20.4762L12.5001 13.4343L19.5357 20.478C19.6601 20.6018 19.8264 20.6705 20.0026 20.6705C20.0897 20.6706 20.176 20.6535 20.2565 20.6202C20.337 20.5869 20.4101 20.5381 20.4717 20.4765C20.5333 20.4149 20.5821 20.3418 20.6154 20.2613C20.6487 20.1808 20.6658 20.0945 20.6657 20.0074C20.6657 19.8305 20.597 19.6637 20.472 19.5405L13.437 12.4962Z'
                className='fill-black group-hover:fill-primaryBlue transition-colors duration-200'
              />
            </svg>
          </button>
          <h2 className='text-[24px] font-bold text-center md:text-left'>
            {t('changePlan')}
          </h2>
          <div className='flex items-center justify-between mt-4 gap-3 flex-col md:flex-row'>
            <p className='text-lightGray max-w-[600px] text-center md:text-left'>
              {t('changePlanDescription')}
            </p>
            <PlanProductType
              tier={productType}
              handleChange={handleProductTypeChange}
            />
          </div>

          <PlanSlider
            plans={filteredPlans.map((p) => p.product)}
            onValueChange={handleSliderCommit}
            unit={unit}
            currentIndex={currentIndex}
          />
          <PlanCard
            isActivePlan={isActivePlan}
            billingCycle={intervalType}
            price={+selectedPlan.amount}
            planFeatures={planFeatures}
            selectedPlan={selectedPlan}
            subscriptionId={activeSubscription.id}
            handleChange={handleIntervalTypeChange}
            onClose={onClose}
          />
        </div>
      </div>
    </>
  )
}

export default ChangePlanModal
